import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { Observable, of } from 'rxjs';
import { Card, CommanderData, Deck } from '@deckplan.io/common';
import { FocusedData } from '@deckplan.io/common/decks/deck';
import { map } from 'rxjs/operators';
import { CardService } from '../../cards/card.service';
import { Theme, ThemeService } from '../../common/theme/theme.service';
import { DecksService } from '../decks.service';
import { AuthService } from '../../auth/auth.service';
import { UsersService } from '../../users/users.service';
import { User } from '@deckplan.io/common/users';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmDialogComponent } from '../../common/confirm-dialog/confirm-dialog.component';

@Component({
    selector: 'app-pretty-deck-list',
    templateUrl: './pretty-deck-list.component.html',
    styleUrls: ['./pretty-deck-list.component.scss']
})
export class PrettyDeckListComponent implements OnInit, OnChanges {

    @Input() public decks: Deck[];
    @Input() public add = false;
    @Input() public isPublicView = false;
    @Output() public addClicked = new EventEmitter<any>();
    @Output() public deleteClicked = new EventEmitter<Deck>();

    public viewButtonColor = 'white';

    public currentUser: User;

    private readonly focusedCardCache = new Map<string, Observable<Card>>();
    private readonly cmcCurveCache = new Map<string, Observable<{ x: number; y: number }[]>>();
    private readonly colorDistroCache = new Map<string, Observable<any>>();
    private readonly cardCountCache = new Map<string, number>();

    constructor(private readonly cardService: CardService,
                private readonly themeService: ThemeService,
                private readonly decksService: DecksService,
                private readonly authService: AuthService,
                private readonly userService: UsersService,
                private readonly dialog: MatDialog,
                private readonly router: Router) { }

    private static calculateDeckCardCount(deck: Deck): number {
        let cardCount = 0;

        for (const card of deck.cards) {
            cardCount += card.quantityInDeck;
        }

        return cardCount;
    }

    public ngOnInit() {
        this.themeService.themeChanges.subscribe(theme => {
            if (theme === Theme.Dark) {
                this.viewButtonColor = 'white';
            } else {
                this.viewButtonColor = 'accent';
            }
        });

        this.updateCurrentUser();
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes.hasOwnProperty('decks') && this.decks) {
            for (const deck of this.decks) {
                this.focusedCardCache.set(deck.id, this.getFocusOrCommander(deck));
                this.cmcCurveCache.set(deck.id, this.createCmcCurve(deck));
                this.colorDistroCache.set(deck.id, this.decksService.colorStats(deck.id).pipe(map(stats => [stats.W, stats.U, stats.B, stats.R, stats.G, stats.C])));
                this.cardCountCache.set(deck.id, PrettyDeckListComponent.calculateDeckCardCount(deck));
            }
        }
    }

    public getFocusedCard(deckId: string): Observable<Card> {
        return this.focusedCardCache.get(deckId);
    }

    public getCmcCurve(deckId: string): Observable<{ x: number; y: number }[]> {
        return this.cmcCurveCache.get(deckId);
    }

    public getColorDistro(deckId) {
        return this.colorDistroCache.get(deckId);
    }

    public getDeckCardCount(deckId: string): number {
        return this.cardCountCache.get(deckId);
    }

    public getRouterLink(deckId) {
        if (this.isPublicView) {
            return `/decks/share/${deckId}`;
        } else {
            return `/decks/${deckId}`;
        }
    }

    public getLikeIconName(deck: Deck) {
        if (!this.currentUser) {
            return 'favorite_border';
        }

        if (this.currentUser.upvotedDeckIds.indexOf(deck.id) !== -1) {
            return 'favorite';
        }

        return 'favorite_border';
    }

    public toggleLikeDeck(deckId) {
        if (this.authService.currentUserId === 'anonymous') {
            this.router.navigate(['/signup', {isPlanner: true}]);
            return;
        }

        if (this.currentUser.upvotedDeckIds.indexOf(deckId) !== -1) {
            this.decksService.removeUpvoteFromDeck(deckId, this.currentUser.id).subscribe((deck) => this.updateForDeckLikes(deck));
        } else {
            this.decksService.addUpvoteToDeck(deckId, this.currentUser.id).subscribe((deck) => this.updateForDeckLikes(deck));
        }
    }

    public confirmDeleteDeck(deckId: string) {
        const deck = this.decks.find((currentDeck) => currentDeck.id === deckId);
        const message = `Are you sure you want to delete ${deck.name}?`;
        const title = 'Confirm Deck Deletion';
        const dialogData = {
            title,
            message
        };

        const dialogRef = this.dialog.open(ConfirmDialogComponent, {
            maxWidth: '800px',
            data: dialogData
        });

        dialogRef.afterClosed().subscribe((dialogResult) => {
            // TODO: Add an output parameter and fire an event if the deletion is selected
            if (dialogResult) {
                console.log('Deleted!');
                this.deleteClicked.emit(deck);
            } else {
                console.log('Not deleted!');
            }
        });
    }

    public shouldShowDeleteButton(deck: Deck): boolean {
        // Check if the current user is the owner of the deck. If so, show them the delete button. If not, don't.
        // The backend will do checking to see if they're actually allowed to delete the deck or not; this is just for GUI cleanliness.
        return  !this.isPublicView && this.currentUser !== undefined && this.currentUser.id === deck.owner.id;
    }

    private getFocusOrCommander(deck: Deck): Observable<Card> {
        if (!deck.specialData) {
            return of({ images: { large: 'https://via.placeholder.com/250x349?text=No Image Available' }} as any);
        }

        let cardId;
        if (deck.format === 'Commander' || deck.format === 'Brawl') {
            cardId = (deck.specialData as CommanderData).commanderId;
        } else {
            cardId = (deck.specialData as FocusedData).focusedId;
        }

        return this.cardService.getCard(cardId);
    }

    private createCmcCurve(deck: Deck) {
        return this.decksService.cmcStats(deck.id).pipe(map(stats => {
            // The range for cmc is 1-16, so our histogram needs that many slots
            // eslint-disable-next-line no-magic-numbers
            const cmcHisto = [...Array(16).keys()].reduce((o, c) => {
                o[c + 1] = 0;
                return o;
            }, {});
            for (const cmc of stats.cmc) {
                if (cmcHisto.hasOwnProperty(cmc)) {
                    cmcHisto[cmc] += 1;
                } else {
                    cmcHisto[cmc] = 1;
                }
            }

            const cmcCurve = [];
            for (const idx in cmcHisto) {
                if (cmcHisto.hasOwnProperty(idx)) {
                    cmcCurve.push({x: parseInt(idx, 10), y: parseInt(cmcHisto[idx], 10)});
                }
            }

            return cmcCurve;
        }));
    }

    private updateCurrentUser() {
        if (this.authService.currentUserId === 'anonymous') {
            this.currentUser = undefined;
        } else {
            this.userService.getOne(this.authService.currentUserId).subscribe((user) => this.currentUser = user);
        }
    }

    private updateForDeckLikes(updatedDeck: Deck) {
        // Pull down the latest upvoted decks for our user
        this.updateCurrentUser();

        this.decks.forEach((deck) => {
            if (deck.id === updatedDeck.id) {
                deck.upvoteCount = updatedDeck.upvoteCount;
            }
        });
    }

}
